import { useEffect, useRef, useState } from 'react';
import type { RunDetail } from '@lyra/shared';
import { Button } from '@/components/ui/button';
import { LiveRegion } from '@/components/a11y/LiveRegion';
import { formatNumberRo } from '@/lib/format';
import { useCancelRun } from './api/runHooks';
import { deriveTiming } from './lib/timing';

function isLive(status: RunDetail['status']): boolean {
  return status === 'queued' || status === 'processing';
}

/**
 * Ported from v1 `useClassification.ts`'s `getCurrentInputRow` (~L65-79):
 * prefer the full input row at `currentRowIndex` (has the real code/name from
 * the uploaded file); fall back to the run's own `currentInputCode`/
 * `currentInputName` snapshot columns when the input row isn't available
 * (e.g. `inputRows` not yet hydrated). Internal `isActiveRunStatus` guard
 * dropped — the caller (`LiveProgress`) already only calls this while live.
 */
function getCurrentInputRow(run: RunDetail): { code: string; name: string } | null {
  if (run.currentRowIndex != null && run.inputRows[run.currentRowIndex]) {
    const row = run.inputRows[run.currentRowIndex];
    return { code: row.code, name: row.name };
  }
  if (run.currentInputCode || run.currentInputName) {
    return { code: run.currentInputCode ?? '', name: run.currentInputName ?? '' };
  }
  return null;
}

/**
 * Live progress panel for an in-flight run — teal progress bar, `n / total`,
 * current input row, live elapsed/avg-per-row ticker, and cancel. Renders
 * only while `run.status` is `queued|processing` (Task 9 brief).
 *
 * Ports v1 `ProcessingStatus.tsx`'s live-stats behaviour (Rule Zero) into
 * Direction B's inline, unboxed idiom:
 *  - `n/total` + percentage bar → same arithmetic, teal fill on a `rule-soft`
 *    track (DESIGN.md "thin 4px meter line"), no card wrapper.
 *  - "Linie curentă" → "Rând curent: {code} — {name}".
 *  - "Timp mediu" / "Timp scurs" → same live ticker: v1 anchors
 *    `processingStartTimeRef` to `run.startedAt` once, then a 1s
 *    `setInterval` recomputes `Date.now() - start` each tick, and
 *    `liveAvgMs = elapsedMsLive / processedInThisRun` (v1's
 *    `processedCountAtRunStart` correction is always hardcoded to `0` in
 *    `useClassification.ts` — dead in practice — so this ports the *actual*
 *    behaviour: no correction, `processedInThisRun === run.processedRows`).
 *  - Cancel button, same `useCancelRun` mutation.
 */
export function LiveProgress({ run }: { run: RunDetail }) {
  const cancelRun = useCancelRun();

  // Forces a re-render every second so the elapsed/avg figures below advance.
  // The interval is cleared on unmount (effect cleanup always runs then) AND
  // the instant the run stops: `run.status` is a dependency, so a
  // queued/processing → completed/failed/cancelled transition re-runs this
  // effect, its cleanup clears the old interval, and the new run short-
  // circuits via `isLive` before scheduling a replacement.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!isLive(run.status)) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [run.status]);

  // Screen-reader progress announcements (Task 3, 2026-08-19 remediation
  // Slice B). Throttled to every 10% boundary — a live region that fires per
  // row (routine runs are 1189 rows) makes a screen reader unusable.
  // `lastMilestoneRef` tracks the highest decile (0/10/…/100) already
  // announced for THIS run so a re-render at the same progress (e.g. the 1s
  // ticker above) doesn't repeat it.
  //
  // Completion is NOT announced from here (fix round, 2026-08-20): this
  // component only renders while `isLive` (the parent, `MaparePage`, unmounts
  // it the instant `run.status` leaves queued/processing — see its own
  // `isProcessing` gate), so an earlier version used the 100% progress
  // milestone (processedRows reaches totalRows) as a same-file proxy for
  // completion. That proxy turned out to almost never fire in practice:
  // `jobRunner.ts` writes the final `processedRows` and calls
  // `finishRun(..., 'completed')` back-to-back with no delay, and the client
  // only polls every 2s — so a real poll landing inside that few-millisecond
  // window is a ~1% event. In the overwhelming majority of runs the client's
  // next poll sees `processing`(<100%) jump straight to `completed`(100%) in
  // one step, `isProcessing` is already false by then, and this component
  // has already unmounted — the 100%-progress render its own proxy depended
  // on never happens. `MaparePage` now owns the completion/failure/
  // cancellation announcement instead, driven directly by observing
  // `run.status` transitions (a mechanism that doesn't depend on this
  // component's mount lifecycle at all).
  const [announcement, setAnnouncement] = useState('');
  const lastMilestoneRef = useRef(-1);
  useEffect(() => {
    if (!isLive(run.status) || run.totalRows <= 0) return;
    const decile = Math.min(100, Math.floor((run.processedRows / run.totalRows) * 10) * 10);
    if (decile <= lastMilestoneRef.current) return;
    lastMilestoneRef.current = decile;
    setAnnouncement(`Se clasifică rândul ${formatNumberRo(run.processedRows)} din ${formatNumberRo(run.totalRows)}`);
  }, [run.status, run.processedRows, run.totalRows]);

  if (!isLive(run.status)) return null;

  // `deriveTiming` always returns nulls here (no `finishedAt` yet while
  // live) — kept only so the live figures below share field names/intent
  // with the post-completion values Task 10 will read from the same helper.
  const { averageTimePerEntry: baseAvgMs } = deriveTiming(run);
  const startedAtMs = run.startedAt ? new Date(run.startedAt).getTime() : null;
  const elapsedMsLive = startedAtMs != null ? Math.max(0, now - startedAtMs) : null;
  const showLive = run.status === 'processing' && elapsedMsLive != null && run.processedRows > 0;
  const liveAvgMs = showLive ? Math.round(elapsedMsLive / run.processedRows) : baseAvgMs;

  const pct = run.totalRows > 0 ? Math.min(100, (run.processedRows / run.totalRows) * 100) : 0;
  const current = getCurrentInputRow(run);
  const cancelPending = cancelRun.isPending && cancelRun.variables === run.id;
  const [confirmingCancel, setConfirmingCancel] = useState(false);

  return (
    <div className="mt-6 space-y-2">
      <LiveRegion message={announcement} />
      <div className="flex items-center gap-2 text-[13.5px] text-ink-2">
        <span className="size-2 shrink-0 rounded-full bg-teal motion-safe:animate-pulse" aria-hidden />
        <span className="tabular-nums">
          {formatNumberRo(run.processedRows)} / {formatNumberRo(run.totalRows)}
        </span>
        <span className="tabular-nums text-ink-3">({Math.round(pct)}%)</span>
      </div>
      <div className="h-1 w-full overflow-hidden rounded-full bg-rule-soft">
        <div className="h-full bg-teal transition-all duration-300" style={{ width: `${pct}%` }} />
      </div>

      {current && (current.code || current.name) && (
        <p className="truncate text-[13.5px] text-ink-3" title={current.name || undefined}>
          Rând curent: <span className="font-mono text-ink-2">{current.code || '—'}</span>
          {current.name ? <> — {current.name}</> : null}
        </p>
      )}

      {showLive && (
        <p className="tabular-nums text-[13.5px] text-ink-3">
          Timp mediu: <span className="font-semibold text-ink-2">{formatNumberRo(liveAvgMs ?? 0)} ms</span>/rând
          {' · '}
          Timp scurs: <span className="font-semibold text-ink-2">{(elapsedMsLive / 1000).toFixed(1)}s</span>
        </p>
      )}

      {confirmingCancel ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[13.5px] text-ink-3">Anulați maparea în curs?</span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => { cancelRun.mutate(run.id); setConfirmingCancel(false); }}
            disabled={cancelPending}
          >
            {cancelPending ? 'Se anulează…' : 'Da, anulează'}
          </Button>
          <button
            type="button"
            onClick={() => setConfirmingCancel(false)}
            className="text-[13.5px] font-medium text-ink-3 hover:text-ink-2"
          >
            Nu
          </button>
        </div>
      ) : (
        <Button type="button" variant="outline" size="sm" onClick={() => setConfirmingCancel(true)} disabled={cancelPending}>
          Anulează
        </Button>
      )}
    </div>
  );
}
